/**
 * Advanced Analytics Dashboard — Super Admin
 * Revenue, processor performance, top earners, equipment and bonus metrics.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Package, DollarSign,
  Award, ShoppingCart, Percent, Activity, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, trendsRes] = await Promise.all([
        api.request('/residuals/analytics'),
        api.request('/residuals/trends?months=12'),
      ]);
      setData(analyticsRes);
      setTrends(trendsRes);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const trendData = trends?.trends || [];
  const summary = trends?.summary || {};
  const maxPayout = Math.max(...trendData.map(t => t.gross_revenue || 0), 1);
  const processors = data?.processor_performance || [];
  const topEarners = data?.top_earners || [];

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading" data-testid="analytics-title">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Revenue performance, trends, and business intelligence</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue', value: `$${(summary.total_earned || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, icon: DollarSign, color: 'text-green-700 bg-green-50', sub: `${trendData.length} months tracked` },
          { label: 'Avg Monthly', value: `$${(summary.avg_monthly_payout || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, icon: TrendingUp, color: 'text-blue-700 bg-blue-50', sub: summary.peak_month !== 'N/A' ? `Peak: ${summary.peak_month}` : 'No data' },
          { label: 'Active Splits', value: data?.splits?.active || 0, icon: Percent, color: 'text-purple-700 bg-purple-50', sub: `${data?.splits?.total || 0} total configured` },
          { label: 'Order Revenue', value: `$${(data?.equipment?.order_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`, icon: ShoppingCart, color: 'text-teal-700 bg-teal-50', sub: `${data?.equipment?.total_orders || 0} orders` },
        ].map(kpi => (
          <div key={kpi.label} className="bg-card border rounded-xl p-5" data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
                <p className="text-2xl font-bold mt-1">{kpi.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{kpi.sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kpi.color}`}>
                <kpi.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Trend Chart (CSS bar chart) */}
      <div className="bg-card border rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" /> Revenue Trend
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Monthly gross revenue over time</p>
          </div>
        </div>
        {trendData.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No historical data yet. Import residual reports to see trends.</p>
          </div>
        ) : (
          <div className="flex items-end gap-2 h-48" data-testid="trend-chart">
            {trendData.map((t, idx) => {
              const height = maxPayout > 0 ? (t.gross_revenue / maxPayout) * 100 : 0;
              return (
                <div key={t.period} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="relative w-full flex justify-center">
                    <div className="absolute -top-6 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground text-background text-[10px] px-2 py-0.5 rounded whitespace-nowrap z-10">
                      ${t.gross_revenue.toLocaleString()}
                    </div>
                    <div
                      className="w-full max-w-[40px] bg-primary/80 rounded-t-md hover:bg-primary transition-colors cursor-pointer"
                      style={{ height: `${Math.max(height, 2)}%` }}
                    />
                  </div>
                  <span className="text-[9px] text-muted-foreground truncate w-full text-center">{t.period}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Processor Performance */}
        <div className="bg-card border rounded-xl p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-primary" /> Processor Performance
          </h3>
          {processors.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">No processor data</p>
          ) : (
            <div className="space-y-3">
              {processors.map(p => {
                const pctOfMax = processors.length > 0 ? (p.gross / Math.max(...processors.map(x => x.gross), 1)) * 100 : 0;
                return (
                  <div key={p.processor} data-testid={`processor-${p.processor}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono px-2 py-0.5 bg-primary/10 text-primary rounded font-semibold">{p.processor}</span>
                        <span className="text-xs text-muted-foreground">{p.merchants} merchants</span>
                      </div>
                      <span className="text-sm font-bold">${p.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pctOfMax}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Earners */}
        <div className="bg-card border rounded-xl p-5">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <Award className="h-4 w-4 text-primary" /> Top Earners
          </h3>
          {topEarners.length === 0 ? (
            <p className="text-center py-8 text-sm text-muted-foreground">No earner data</p>
          ) : (
            <div className="space-y-3">
              {topEarners.map((e, idx) => (
                <div key={e.user_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30" data-testid={`earner-${idx}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-amber-100 text-amber-700' :
                    idx === 1 ? 'bg-slate-200 text-slate-700' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{e.name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{e.role || ''} — {e.report_count} reports</p>
                  </div>
                  <p className="text-sm font-bold text-green-700">${e.total_payout?.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border rounded-xl p-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Equipment Products</p>
          <p className="text-xl font-bold mt-1">{data?.equipment?.total_products || 0}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total Bonuses Paid</p>
          <p className="text-xl font-bold text-green-700 mt-1">${(data?.bonuses?.total_amount || 0).toFixed(2)}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Bonus Count</p>
          <p className="text-xl font-bold mt-1">{data?.bonuses?.count || 0}</p>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Residual Reports</p>
          <p className="text-xl font-bold mt-1">{data?.reports_total || 0}</p>
        </div>
      </div>
    </div>
  );
}
